import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Share, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { formatDistanceToNow } from "date-fns";
import { GlobalLoadingIndicator } from "@/components/layout/GlobalLoadingIndicator";
import { isPublicSurfaceRoute } from "./public-surface";

const MANIFEST_LINK_ID = "pwa-manifest";
const SERVICE_WORKER_URL = "/sw.js";

const PWA_ROUTE_PREFIXES = [
  "/member",
  "/portal",
  "/attendance",
  "/kids-check-in",
  "/volunteer",
  "/public",
  "/b/",
];

const PWA_APP_LAYOUT_PREFIXES = [
  "/member",
  "/portal",
  "/attendance",
  "/kids-check-in",
  "/volunteer",
];

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

function isPwaRoute(pathname: string) {
  return PWA_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
}

function isPwaAppLayoutRoute(pathname: string) {
  return PWA_APP_LAYOUT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix)
  );
}

const INSTALL_DISMISSED_KEY = "forge:pwa-install-dismissed";
const INSTALL_SESSION_KEY = "forge:pwa-install-session-dismissed";
const INSTALL_DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // Re-prompt after 7 days

function getPersistedDismissal(): boolean {
  try {
    // Check session-level dismissal first (never re-show in same session)
    if (sessionStorage.getItem(INSTALL_SESSION_KEY)) return true;

    const raw = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < INSTALL_DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function persistDismissal() {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
    sessionStorage.setItem(INSTALL_SESSION_KEY, "1");
  } catch {
    // Ignore write errors
  }
}

function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

function isStandaloneMode(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

// Minimum time (ms) user must be on a supported route before showing install prompt
const INSTALL_PROMPT_DELAY_MS = 30_000;

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(getPersistedDismissal);
  const [promptReady, setPromptReady] = useState(false);
  const isSupportedRoute = useMemo(() => isPwaRoute(location.pathname), [location.pathname]);
  const isAppLayoutRoute = useMemo(
    () => isPwaAppLayoutRoute(location.pathname),
    [location.pathname]
  );

  // Delay install prompt until user has been on a supported route for a minimum duration
  useEffect(() => {
    if (!isSupportedRoute) {
      setPromptReady(false);
      return;
    }
    const timer = setTimeout(() => setPromptReady(true), INSTALL_PROMPT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isSupportedRoute]);

  useEffect(() => {
    if (!isSupportedRoute) {
      const existing = document.getElementById(MANIFEST_LINK_ID);
      if (existing) {
        existing.remove();
      }
      return;
    }

    let link = document.getElementById(MANIFEST_LINK_ID) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = MANIFEST_LINK_ID;
      link.rel = "manifest";
      link.href = "/manifest.json";
      document.head.appendChild(link);
    }
  }, [isSupportedRoute]);

  useEffect(() => {
    if (!isSupportedRoute || !("serviceWorker" in navigator)) {
      return;
    }

    navigator.serviceWorker.register(SERVICE_WORKER_URL).catch((error) => {
      console.error("Service worker registration failed", error);
    });
  }, [isSupportedRoute]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setInstallEvent(null);
      setDismissed(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const showInstallPrompt = isSupportedRoute && promptReady && installEvent && !dismissed;
  const showIOSGuide =
    isSupportedRoute && promptReady && !dismissed && !installEvent && isIOSSafari() && !isStandaloneMode();

  const handleInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "dismissed") {
      setDismissed(true);
      persistDismissal();
    }
    setInstallEvent(null);
  };

  const handleDismissInstall = () => {
    setDismissed(true);
    persistDismissal();
  };

  // Sync telemetry is for signed-in app surfaces only — never on public/donor
  // routes (the banner itself also guards against public routes, so a future
  // mount elsewhere cannot reintroduce it on those surfaces).
  const showSyncStatus =
    isSupportedRoute && !isAppLayoutRoute && !isPublicSurfaceRoute(location.pathname);

  return (
    <>
      <GlobalLoadingIndicator />
      {children}
      {showSyncStatus ? <AppShellSyncBanner /> : null}
      {showInstallPrompt ? (
        <div className="pointer-events-none fixed bottom-4 right-4 z-50 w-full max-w-sm px-4 sm:px-0">
          <Alert className="pointer-events-auto border border-border/60 bg-background/95 shadow-lg">
            <AlertTitle className="text-section-title font-semibold">Install Forge</AlertTitle>
            <AlertDescription className="mt-1 text-meta text-muted-foreground">
              Add Forge to your home screen for faster access and a full-screen experience.
            </AlertDescription>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="primary" onClick={handleInstall}>
                Install app
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismissInstall}>
                Not now
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}
      {showIOSGuide ? (
        <div className="pointer-events-none fixed bottom-4 right-4 z-50 w-full max-w-sm px-4 sm:px-0">
          <Alert className="pointer-events-auto border border-border/60 bg-background/95 shadow-lg">
            <AlertTitle className="flex items-center gap-2 text-section-title font-semibold">
              <Share className="h-4 w-4" />
              Add Forge to your Home Screen
            </AlertTitle>
            <AlertDescription className="mt-1 space-y-2 text-meta text-muted-foreground">
              <p>Get app-like access without the App Store:</p>
              <ol className="list-inside list-decimal space-y-1 text-meta">
                <li>
                  Tap the <strong>Share</strong> button in Safari's toolbar
                </li>
                <li>
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </li>
                <li>
                  Tap <strong>Add</strong> to confirm
                </li>
              </ol>
            </AlertDescription>
            <div className="mt-4">
              <Button size="sm" variant="ghost" onClick={handleDismissInstall}>
                Got it
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}
    </>
  );
}

/** How long the zero-information "everything is healthy" state stays visible. */
export const SYNC_BANNER_HEALTHY_HIDE_MS = 4_000;

export const AppShellSyncBanner = () => {
  const location = useLocation();
  const { isOnline } = useNetworkStatus();
  const { pendingCount, isSyncing, lastSyncAt, syncError, performSync } = useOfflineSync();
  const [dismissed, setDismissed] = useState(false);
  const [healthyExpired, setHealthyExpired] = useState(false);

  // Healthy = nothing actionable to report. This state auto-hides; offline,
  // queued actions, active syncs, and errors stay visible persistently.
  const isHealthy = isOnline && pendingCount === 0 && !isSyncing && !syncError;

  // Signature of the states that matter to a user. When it changes (went
  // offline, new queued work, new error), any prior dismissal is cleared so
  // the banner reappears.
  const stateSignature = `${isOnline}|${pendingCount}|${syncError ?? ""}`;

  useEffect(() => {
    setDismissed(false);
    setHealthyExpired(false);
    if (!isHealthy) return;
    const timer = setTimeout(() => setHealthyExpired(true), SYNC_BANNER_HEALTHY_HIDE_MS);
    return () => clearTimeout(timer);
  }, [stateSignature, isHealthy]);

  // Defense in depth: even if a future caller mounts this banner globally,
  // internal sync telemetry never renders on public/donor surfaces.
  if (isPublicSurfaceRoute(location.pathname)) return null;
  if (dismissed || (isHealthy && healthyExpired)) return null;

  const pendingLabel =
    pendingCount > 0
      ? `${pendingCount} queued ${pendingCount === 1 ? "action" : "actions"}`
      : "No queued actions";
  const lastSyncLabel = lastSyncAt
    ? `Last sync ${formatDistanceToNow(lastSyncAt, { addSuffix: true })}`
    : "No recent sync";
  const syncDetail = syncError ?? (isSyncing ? "Syncing now" : lastSyncLabel);
  const statusTone = !isOnline
    ? "border-warning-200/70 bg-warning-50"
    : pendingCount > 0
      ? "border-info-200/70 bg-info-50"
      : "border-success-200/70 bg-success-50";

  return (
    <div
      className="pointer-events-none fixed left-4 z-40 w-full max-w-sm px-4 sm:px-0"
      style={{ bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <Alert className={`pointer-events-auto relative border pr-10 shadow-lg ${statusTone}`}>
        <button
          type="button"
          aria-label="Dismiss sync status"
          onClick={() => setDismissed(true)}
          className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <AlertTitle className="text-section-title font-semibold">
          {isOnline ? "Online" : "Offline"}
        </AlertTitle>
        <AlertDescription className="mt-1 space-y-1 text-meta text-muted-foreground">
          <div className="flex flex-col gap-1">
            <span>{isOnline ? "Working online." : "You're offline. Changes will sync automatically."}</span>
            <span>{pendingLabel}</span>
            <span>{syncDetail}</span>
          </div>
        </AlertDescription>
        {isOnline && pendingCount > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => performSync()}>
              Sync now
            </Button>
          </div>
        ) : null}
      </Alert>
    </div>
  );
};
